import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../services/auth';
import { useToast } from '../contexts/ToastContext';
import PasswordInput from './PasswordInput';
import {
  calculatePasswordStrength,
  validatePassword,
  type PasswordStrength,
} from '../utils/passwordValidation';
import './PasswordResetForm.css';

interface PasswordResetFormProps {
  onSuccess?: () => void;
  onBackToLogin?: () => void;
  className?: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  token?: string;
  general?: string;
}

interface ForgotPasswordData {
  email: string;
}

interface ResetPasswordData {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  onSuccess,
  onBackToLogin,
  className = '',
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const { showSuccess, showError } = useToast();

  // Determine if this is a reset flow (has token) or forgot password flow
  const isResetFlow = !!resetToken;

  // Form state
  const [forgotPasswordData, setForgotPasswordData] =
    useState<ForgotPasswordData>({
      email: '',
    });

  const [resetPasswordData, setResetPasswordData] = useState<ResetPasswordData>(
    {
      token: resetToken || '',
      newPassword: '',
      confirmPassword: '',
    },
  );

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    feedback: [],
    isValid: false,
  });

  // Update token if it changes in URL
  useEffect(() => {
    if (resetToken) {
      setResetPasswordData((prev) => ({ ...prev, token: resetToken }));
    }
  }, [resetToken]);

  // Update password strength when password changes
  useEffect(() => {
    const strength = calculatePasswordStrength(resetPasswordData.newPassword);
    setPasswordStrength(strength);
  }, [resetPasswordData.newPassword]);

  // Email validation
  const validateEmail = (email: string): string | undefined => {
    if (!email) {
      return 'Email is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }

    return undefined;
  };

  // Confirm password validation
  const validateConfirmPassword = (
    confirmPassword: string,
    password: string,
  ): string | undefined => {
    if (!confirmPassword) {
      return 'Please confirm your password';
    }

    if (confirmPassword !== password) {
      return 'Passwords do not match';
    }

    return undefined;
  };

  // Token validation
  const validateToken = (token: string): string | undefined => {
    if (!token) {
      return 'Reset token is required';
    }

    if (token.length < 10) {
      return 'Invalid reset token';
    }

    return undefined;
  };

  // Real-time validation
  const validateField = (
    name: string,
    value: string,
    additionalData?: any,
  ): string | undefined => {
    switch (name) {
      case 'email':
        return validateEmail(value);
      case 'newPassword':
        return validatePassword(value);
      case 'confirmPassword':
        return validateConfirmPassword(
          value,
          additionalData?.newPassword || '',
        );
      case 'token':
        return validateToken(value);
      default:
        return undefined;
    }
  };

  // Handle forgot password form input change
  const handleForgotPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;

    setForgotPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors when user starts typing
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }

    // Real-time validation for touched fields
    if (touched[name]) {
      const fieldError = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: fieldError,
      }));
    }
  };

  // Handle reset password form input change
  const handleResetPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;

    setResetPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors when user starts typing
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }

    // Real-time validation for touched fields
    if (touched[name]) {
      const fieldError = validateField(name, value, resetPasswordData);
      const errorKey = name === 'newPassword' ? 'password' : name;
      setErrors((prev) => ({
        ...prev,
        [errorKey]: fieldError,
      }));
    }
  };

  // Handle field blur
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setTouched((prev) => ({ ...prev, [name]: true }));

    const fieldError = validateField(name, value, resetPasswordData);
    const errorKey = name === 'newPassword' ? 'password' : name;
    setErrors((prev) => ({
      ...prev,
      [errorKey]: fieldError,
    }));
  };

  // Validate forgot password form
  const validateForgotPasswordForm = (): boolean => {
    const emailError = validateEmail(forgotPasswordData.email);

    const newErrors: FormErrors = {
      email: emailError,
    };

    setErrors(newErrors);
    setTouched({ email: true });

    return !emailError;
  };

  // Validate reset password form
  const validateResetPasswordForm = (): boolean => {
    const tokenError = validateToken(resetPasswordData.token);
    const passwordError = validatePassword(resetPasswordData.newPassword);
    const confirmPasswordError = validateConfirmPassword(
      resetPasswordData.confirmPassword,
      resetPasswordData.newPassword,
    );

    const newErrors: FormErrors = {
      token: tokenError,
      password: passwordError,
      confirmPassword: confirmPasswordError,
    };

    setErrors(newErrors);
    setTouched({
      token: true,
      newPassword: true,
      confirmPassword: true,
    });

    return !tokenError && !passwordError && !confirmPasswordError;
  };

  // Handle forgot password submission
  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForgotPasswordForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await authApi.forgotPassword(forgotPasswordData.email);

      if (response.data.success) {
        showSuccess(
          'If an account with this email exists, reset instructions will be sent shortly.',
          { duration: 6000 },
        );

        // Clear the form after successful submission
        setForgotPasswordData({ email: '' });
        setTouched({});
        setErrors({});
      }
    } catch (err: any) {
      console.error('Forgot password failed:', err);

      // Handle specific error cases
      if (err.code === 'USER_NOT_FOUND') {
        showError('No account found with this email address');
      } else if (err.response?.data?.error === 'USER_NOT_FOUND') {
        showError('No account found with this email address');
      } else {
        showError(
          err.message || 'Failed to send reset instructions. Please try again.',
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reset password submission
  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateResetPasswordForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await authApi.resetPassword(
        resetPasswordData.token,
        resetPasswordData.newPassword,
      );

      if (response.data.success) {
        showSuccess(
          'Your password has been successfully reset. You can now sign in with your new password.',
          { duration: 6000 },
        );

        // Redirect to login after successful reset
        setTimeout(() => {
          onSuccess?.();
          navigate('/login');
        }, 2000);
      }
    } catch (err: any) {
      console.error('Reset password failed:', err);

      // Handle specific error cases
      if (err.code === 'INVALID_TOKEN' || err.code === 'TOKEN_EXPIRED') {
        setErrors({
          token:
            'This reset link is invalid or has expired. Please request a new one.',
        });
      } else if (err.code === 'WEAK_PASSWORD') {
        setErrors({
          password: 'Password does not meet security requirements',
        });
      } else {
        showError(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle back to login
  const handleBackToLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    onBackToLogin?.();
    navigate('/login');
  };

  // Password strength indicator helper functions
  const getStrengthClass = (score: number): string => {
    if (score === 0) return '';
    if (score <= 2) return 'weak';
    if (score <= 3) return 'medium';
    return 'strong';
  };

  const getStrengthText = (score: number): string => {
    if (score === 0) return '';
    if (score <= 2) return 'Weak';
    if (score <= 3) return 'Medium';
    return 'Strong';
  };

  // Check if forms are valid
  const isForgotPasswordValid = forgotPasswordData.email && !errors.email;
  const isResetPasswordValid =
    resetPasswordData.token &&
    resetPasswordData.newPassword &&
    resetPasswordData.confirmPassword &&
    !errors.token &&
    !errors.password &&
    !errors.confirmPassword &&
    passwordStrength.isValid;

  return (
    <div className={`password-reset-form ${className}`}>
      {isResetFlow ? (
        // Reset Password Form (with token)
        <form onSubmit={handleResetPasswordSubmit} noValidate role="form">
          <div className="form-header">
            <h2>Reset Your Password</h2>
            <p>Enter your new password below.</p>
          </div>

          {/* General error message */}
          {errors.general && (
            <div className="error-message" role="alert">
              {errors.general}
            </div>
          )}

          {/* Hidden token field for accessibility */}
          <input type="hidden" name="token" value={resetPasswordData.token} />

          {/* Token error (if any) */}
          {errors.token && (
            <div className="error-message" role="alert">
              {errors.token}
            </div>
          )}

          {/* New Password field */}
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              New Password
            </label>
            <PasswordInput
              id="newPassword"
              name="newPassword"
              value={resetPasswordData.newPassword}
              onChange={handleResetPasswordChange}
              onBlur={handleBlur}
              placeholder="Enter your new password"
              disabled={isLoading}
              autoComplete="new-password"
              hasError={!!errors.password}
              aria-describedby={`${errors.password ? 'password-error' : ''} password-strength`}
              aria-invalid={!!errors.password}
            />
            {errors.password && (
              <span id="password-error" className="field-error" role="alert">
                {errors.password}
              </span>
            )}

            {/* Password strength indicator */}
            {resetPasswordData.newPassword && (
              <div
                id="password-strength"
                className="password-strength"
                aria-live="polite"
              >
                <div className="strength-bar">
                  <div
                    className={`strength-fill ${getStrengthClass(passwordStrength.score)}`}
                    style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                  />
                </div>
                <div className="strength-info">
                  <span
                    className={`strength-text ${getStrengthClass(passwordStrength.score)}`}
                  >
                    {getStrengthText(passwordStrength.score)}
                  </span>
                  {passwordStrength.feedback.length > 0 && (
                    <ul className="strength-feedback">
                      {passwordStrength.feedback.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password field */}
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm New Password
            </label>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              value={resetPasswordData.confirmPassword}
              onChange={handleResetPasswordChange}
              onBlur={handleBlur}
              placeholder="Confirm your new password"
              disabled={isLoading}
              autoComplete="new-password"
              hasError={!!errors.confirmPassword}
              aria-describedby={
                errors.confirmPassword ? 'confirm-password-error' : undefined
              }
              aria-invalid={!!errors.confirmPassword}
            />
            {errors.confirmPassword && (
              <span
                id="confirm-password-error"
                className="field-error"
                role="alert"
              >
                {errors.confirmPassword}
              </span>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            className={`submit-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading || !isResetPasswordValid}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner" aria-hidden="true"></span>
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>

          {/* Back to login link */}
          <div className="form-footer">
            <button
              type="button"
              className="back-to-login-link"
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              Back to Sign In
            </button>
          </div>
        </form>
      ) : (
        // Forgot Password Form (email input)
        <form onSubmit={handleForgotPasswordSubmit} noValidate role="form">
          <div className="form-header">
            <h2>Forgot Your Password?</h2>
            <p>
              Enter your email address and we'll send you instructions to reset
              your password.
            </p>
          </div>

          {/* General error message */}
          {errors.general && (
            <div className="error-message" role="alert">
              {errors.general}
            </div>
          )}

          {/* Email field */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={forgotPasswordData.email}
              onChange={handleForgotPasswordChange}
              onBlur={handleBlur}
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder="Enter your email address"
              disabled={isLoading}
              autoComplete="email"
              aria-describedby={errors.email ? 'email-error' : undefined}
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <span id="email-error" className="field-error" role="alert">
                {errors.email}
              </span>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            className={`submit-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading || !isForgotPasswordValid}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner" aria-hidden="true"></span>
                Sending Instructions...
              </>
            ) : (
              'Send Reset Instructions'
            )}
          </button>

          {/* Back to login link */}
          <div className="form-footer">
            <button
              type="button"
              className="back-to-login-link"
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              Back to Sign In
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PasswordResetForm;
